/**
 * TourShell — the shared full-screen slide engine behind every How It Works
 * chapter (Story / Workflow / Around the Site).
 *
 * Owns navigation (click-anywhere, arrows/Space/Home/End, Escape), the
 * per-slide accent backdrop glow, progress dots, sound toggles and the
 * optional finale (CTA button + confetti). What a slide LOOKS like is the
 * chapter's business — passed in via `renderSlide`. The audio engine is
 * hoisted by the chapter router and passed down so music keeps playing
 * across hub ↔ chapter navigation.
 */
import { ArrowBackIosNew, ArrowForwardIos, Close } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { TourAudioControls } from './TourAudioControls';
import { ConfettiBurst, FloatingParticles } from './TourEffects';
import { accentColor, tourBackdropSx, type TourSlideBase } from './tourCore';
import type { TourAudio } from './useTourAudio';
import AlisWordmark from '../../components/brand/AlisWordmark';

const glowIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.55; transform: translateY(0); }
  50%      { opacity: 1; transform: translateY(-3px); }
`;

export interface TourFinale {
  /** Rain confetti on the last slide (the Story's signature move). */
  confetti?: boolean;
  ctaLabel: string;
  onCta: () => void;
}

interface TourShellProps<S extends TourSlideBase> {
  slides: readonly S[];
  /** Uppercase chapter label in the top chrome, e.g. "The Story". */
  chapterLabel: string;
  renderSlide: (slide: S, direction: 1 | -1) => ReactNode;
  /** Escape / X destination (normally back to the hub). */
  onExit: () => void;
  audio: TourAudio;
  finale?: TourFinale;
  startHint?: string;
}

export const TourShell = <S extends TourSlideBase>({
  slides,
  chapterLabel,
  renderSlide,
  onExit,
  audio,
  finale,
  startHint = '✦ Click anywhere to begin ✦',
}: TourShellProps<S>) => {
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);

  const last = slides.length - 1;
  const slide = slides[index];
  const accent = accentColor(slide.accent, theme);

  // Preload this chapter's artwork up front so transitions never flash.
  useEffect(() => {
    slides.forEach((s) => {
      const img = new Image();
      img.src = s.image;
    });
  }, [slides]);

  const exitTour = useCallback(() => {
    audio.play('click');
    onExit();
  }, [audio, onExit]);

  const goTo = useCallback(
    (next: number, dir: 1 | -1) => {
      audio.unlock();
      if (next < 0 || next > last || next === index) return;
      setDirection(dir);
      setIndex(next);
      audio.play(next === last && finale ? 'finale' : 'whoosh');
    },
    [audio, index, last, finale],
  );

  const goNext = useCallback(() => goTo(index + 1, 1), [goTo, index]);
  const goPrev = useCallback(() => goTo(index - 1, -1), [goTo, index]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
        case 'Enter':
        case 'PageDown':
          e.preventDefault();
          goNext();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          goPrev();
          break;
        case 'Home':
          goTo(0, -1);
          break;
        case 'End':
          goTo(last, 1);
          break;
        case 'Escape':
          exitTour();
          break;
        default:
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrev, goTo, exitTour, last]);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <Box onClick={goNext} sx={{ ...tourBackdropSx(theme), cursor: 'pointer', userSelect: 'none' }}>
      {/* Accent glow — remounts per slide and fades in over the dark base */}
      <Box
        key={`glow-${index}`}
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          animation: `${glowIn} 1.4s ease-out both`,
          backgroundImage: `radial-gradient(ellipse 90% 70% at 72% 42%, ${alpha(accent, 0.30)} 0%, transparent 62%),
            radial-gradient(ellipse 60% 55% at 15% 85%, ${alpha(accent, 0.16)} 0%, transparent 70%)`,
        }}
      />
      <FloatingParticles />

      {/* Slide (remounted so each chapter's entrance choreography replays) */}
      <Box key={slide.id} sx={{ position: 'absolute', inset: 0 }}>
        {renderSlide(slide, direction)}
      </Box>

      {index === last && finale?.confetti && <ConfettiBurst />}

      {/* ── Top chrome ─────────────────────────────────────────────── */}
      <Box
        onClick={stop}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, md: 4 },
          py: 2,
          cursor: 'default',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AlisWordmark sx={{ opacity: 0.9 }} />
          <Typography
            sx={{
              color: alpha(theme.palette.common.white, 0.55),
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {chapterLabel}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TourAudioControls audio={audio} />
          <Tooltip title="Back to chapters">
            <IconButton onClick={exitTour} sx={{ color: alpha(theme.palette.common.white, 0.7), ml: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* First-slide hint */}
      {index === 0 && (
        <Typography
          sx={{
            position: 'absolute',
            bottom: 88,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: alpha(theme.palette.common.white, 0.75),
            fontSize: '0.85rem',
            letterSpacing: '0.08em',
            animation: `${pulse} 2.4s ease-in-out infinite`,
            pointerEvents: 'none',
          }}
        >
          {startHint}
        </Typography>
      )}

      {/* Finale call-to-action */}
      {index === last && finale && (
        <Box onClick={stop} sx={{ position: 'absolute', bottom: 96, left: 0, right: 0, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => { audio.play('chime'); finale.onCta(); }}
            sx={{
              px: 5,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 700,
              borderRadius: `${theme.border.radius * 2}px`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.55)}`,
              animation: `${glowIn} 0.8s 0.9s ease-out both`,
            }}
          >
            {finale.ctaLabel}
          </Button>
        </Box>
      )}

      {/* ── Bottom chrome: prev / dots / next ──────────────────────── */}
      <Box
        onClick={stop}
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1.5, md: 3 },
          py: 2.5,
          cursor: 'default',
        }}
      >
        <IconButton
          onClick={goPrev}
          disabled={index === 0}
          aria-label="Previous"
          sx={{
            color: theme.palette.common.white,
            backgroundColor: alpha(theme.palette.common.white, 0.08),
            '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.18) },
            '&.Mui-disabled': { color: alpha(theme.palette.common.white, 0.2) },
          }}
        >
          <ArrowBackIosNew fontSize="small" />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {slides.map((s, i) => (
            <Box
              key={s.id}
              onClick={() => goTo(i, i > index ? 1 : -1)}
              role="button"
              aria-label={`Go to: ${s.kicker}`}
              sx={{
                width: i === index ? 26 : 9,
                height: 9,
                borderRadius: 5,
                cursor: 'pointer',
                transition: 'all 0.35s ease',
                backgroundColor:
                  i === index
                    ? theme.schedule.tentative
                    : alpha(theme.palette.common.white, i < index ? 0.55 : 0.22),
                '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.75) },
              }}
            />
          ))}
        </Box>

        <IconButton
          onClick={goNext}
          disabled={index === last}
          aria-label="Next"
          sx={{
            color: theme.palette.common.white,
            backgroundColor: alpha(theme.schedule.tentative, index === last ? 0.08 : 0.35),
            '&:hover': { backgroundColor: alpha(theme.schedule.tentative, 0.55) },
            '&.Mui-disabled': { color: alpha(theme.palette.common.white, 0.2) },
          }}
        >
          <ArrowForwardIos fontSize="small" />
        </IconButton>

        <Typography
          sx={{
            position: 'absolute',
            right: { xs: 12, md: 28 },
            color: alpha(theme.palette.common.white, 0.4),
            fontSize: '0.75rem',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {index + 1} / {slides.length}
        </Typography>
      </Box>
    </Box>
  );
};
